import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next, once } from '@ember/runloop';
import { service } from '@ember/service';

import $ from 'jquery';

import type { APIData, DSSetup, Gridboard, RecordData, Service } from 'webapp-types';
import { GenieBuildBoardEntryPoint } from 'webapp/enums/ai-chat';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { isAlive } from 'webapp/utils/common';
import { isNumber } from 'webapp/utils/number';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockWrapperComponent from 'webapp/components/block/wrapper';
import type { BoardBlocksBaseArgs } from 'webapp/components/board/blocks/base';
import BoardBlocksBaseComponent from 'webapp/components/board/blocks/base';

import type Block from 'webapp/models-custom/block';
import Board from 'webapp/models-custom/board';
import type { GridNode, GridPosition, GridSize } from 'webapp/models-custom/datawall-grid';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import NoRerenderSortable from 'webapp/models-custom/no-rerender-sortable';

import { Severity } from 'webapp/services/logger';

interface BoardBlocksWallArgs extends BoardBlocksBaseArgs {
	disableBlockSizeAnimations?: boolean;
	isPortraitView?: boolean;
	onDatawallGridChange: (datawallGrid?: DatawallGrid | null) => void;
	trackingOrigin?: string;
}

export default class BoardBlocksWallComponent extends BoardBlocksBaseComponent<BoardBlocksWallArgs> {
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/data-selects')
	declare dataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('flow/genie-board-build')
	declare genieBoardBuildService: Service.Flow.GenieBoardBuild;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;

	availableTourIds = [TourId.DESIGNER__MOVE_BLOCK, TourId.DESIGNER__RESIZE_BLOCK];

	constructor(owner: Owner, args: BoardBlocksWallArgs) {
		super(owner, args);

		this.noRerenderSortable = new NoRerenderSortable<Block>({
			elementSelector: '.gridboard-item',
			dataIdProperty: 'localId',
			dataSortProperty: 'order',
			skipSortByDataSortProperty: true, // no sort needed
			getModel: () => {
				return this.blocks;
			},
			onNodeDelete: (el) => {
				if (this.activeDatawall) {
					this.activeDatawall.removeElement(el);
				}
			},
		});

		this.prepareGrid();
	}

	get minSize(): number {
		return DatawallGrid.minimumSize;
	}

	get maxWidth(): number {
		return this.args.isPortraitView ? DatawallGrid.wallHeight : DatawallGrid.wallWidth;
	}

	get maxHeight(): number {
		return this.args.isPortraitView ? DatawallGrid.wallWidth : DatawallGrid.wallHeight;
	}

	datawallGridModelChange(): void {
		this.args.onDatawallGridChange(this.activeDatawall);
	}

	// ------ TOUR ------

	checkTourStartConditions(): boolean {
		return isAlive(this) && !!this.args.boardState.selectedBlock;
	}

	selectFirstBlockForTour(): void {
		const blocks = this.blocks.filter((block) => !block.model.isGenerating),
			boardState = this.args.boardState;

		if (boardState.selectedBlock) {
			return;
		}

		if (boardState.isViewOnly || blocks.length === 0) {
			return;
		}

		const firstBlock = blocks[0]!;
		this.eventManagerService.trigger(AppEvent.BLOCK_SELECT);
		this.navigationService.transitionToRoute(
			'designer.board.block',
			firstBlock.board!,
			firstBlock.model,
		);
	}

	initGrid(): void {
		const el = this.el;

		if (!el.find('.gridboard.blocks').length) {
			return;
		}

		const options: Gridboard.Options = {
			staticGrid: this.args.isViewOnly,
		};

		let placeholdersEl;

		if (!this.args.isViewOnly) {
			options.draggable = {
				distance: 3,
				// handle: '.active-block span.sort',
				cancel: '.bc__title, .bc__html-content', // otherwise click on story / text / ai summary edit does not focus editor
				start: (_event: JQuery.Event, ui: jQueryUIInstance) => {
					this.flowTourDesignerService.isTemporaryBlockerActive = true;
					if (_event.type === 'resizestart') {
						this.flowTourDesignerService.endTour(TourId.DESIGNER__RESIZE_BLOCK);
					}

					if (_event.type === 'dragstart') {
						this.flowTourDesignerService.endTour(TourId.DESIGNER__MOVE_BLOCK);
					}

					$('.highcharts-tooltip-container').css({ opacity: 0 }); // hide chart tooltip when dragging
					try {
						ui.helper.find('.active-block span.sort').tooltipster('instance')!.close();
					} catch (_e) {
						// skip
					}
				},
				stop: (_event: JQuery.Event, _ui: jQueryUIInstance) => {
					this.flowTourDesignerService.isTemporaryBlockerActive = false;
					this.flowTourDesignerService.activeTour?.repositionBubble(); // we reposition bubble, if the user is doing the "wrong" action

					$('.highcharts-tooltip-container').css({ opacity: 1 }); // show chart tooltip again
					later(() => {
						// to tell tooltips the block has been moved
						el.get(0)
							?.querySelector('.bc__chart')
							?.dispatchEvent(new CustomEvent('blockPositionChanged'));
					}, 300); // after block has "settled" in its position
				},
			};

			options.onChange = (_event: JQuery.Event, items: GridNode[]) => {
				if (!items) {
					return;
				}

				items.forEach((item: GridNode) => {
					const block = this.blocks.find((b) => b.localId === +item.id);

					if (block?.id) {
						const positions = {
							x: item.x,
							y: item.y,
							w: item.width,
							h: item.height,
						};

						if (block.model.isGenerating) {
							// hold still, then dissapear when the real block appears
							item.el?.draggable('disable');
						}

						if (
							typeof positions.x !== 'number' ||
							typeof positions.y !== 'number' ||
							isNaN(positions.x) ||
							isNaN(positions.y)
						) {
							next(() => {
								this.fixBlocksWithoutPositions();
							});
							return;
						}

						// if values are the same, skip
						if (
							block.posX === positions.x &&
							block.posY === positions.y &&
							block.width === positions.w &&
							block.height === positions.h
						) {
							return;
						}

						// check if coordinates are inside bounds
						if (
							positions.x + positions.w > DatawallGrid.wallWidth ||
							positions.y + positions.h > DatawallGrid.wallHeight
						) {
							this.logger.logError(
								'Wall block out of bounds',
								{
									blockId: block.id,
								},
								Severity.CRITICAL,
							);
						} else {
							this.updateAndSavePosition(block, positions);
						}
					}
				});
			};
			options.onDrop = (
				_event: JQuery.Event,
				_originalNode: unknown,
				node: GridNode,
				ui: jQueryUIInstance,
			) => {
				const setupIdTmp = ui.helper.data('setupid') as number | undefined; // a numeric data attribute
				const setupId = isNumber(setupIdTmp) ? +setupIdTmp : undefined; // set only for empty new visualizations

				void this.getBlockDataFromDropEvent(ui.helper).then((blockData) => {
					blockData.posX = node.x;
					blockData.posY = node.y;
					blockData.width = node.width;
					blockData.height = node.height;

					// added for tracking
					blockData.metricType = ui.helper.data('metrictype') as string | undefined;

					blockData.fromMetricDateRange =
						this.args.boardState.commonMasterDateRange?.formula;

					this.addNewBlock(blockData, setupId, ui.helper);
				});
			};

			placeholdersEl = el.find('.gridboard.placeholders');
		}

		// we disable gridstack block animations when datawall is in embed mode (?i)
		// chrome seems to block css animations until iframe (from diff domain) is in viewport
		// which means if datawall is not in viewport after we close fullscreen,
		// the gridboard-item css heights are in pre-animated size and the render is faulty
		// bug task https://app.asana.com/0/8014827585025/1127162467020381
		if (this.args.disableBlockSizeAnimations) {
			options.animate = false;
		}

		if (this.args.isPortraitView) {
			options.width = DatawallGrid.wallHeight;
			options.height = DatawallGrid.wallWidth;
		}

		const datawallGrid = new DatawallGrid();
		datawallGrid.initMainGrid(el.find('.gridboard.blocks'), options, placeholdersEl);

		this.activeDatawall = datawallGrid;
		this.datawallGridModelChange();
	}

	// ------ Position updating ------

	positionsToUpdate: APIData.Block.RepositionRequestItem[] = [];
	_repositionRequestsActive = 0;
	_respositionErrorLogged = false;

	updatePositions(): void {
		const data = this.positionsToUpdate;

		if (!data.length) {
			return;
		}

		if (!this.board.canCurrentUserEdit) {
			return;
		}

		if (this.navigationService.isDatawallApp) {
			return;
		}

		// set active requests hard limit
		if (this._repositionRequestsActive >= 8) {
			if (!this._respositionErrorLogged) {
				this.logger.logError('Wall block reposition spam', {
					blockId: data[0]?.blockId,
				});
				this._respositionErrorLogged = true;
			}

			if (this._repositionRequestsActive >= 15) {
				this.logger.logError('Wall block reposition spam [THROW]');
				this.navigationService.transitionToRoute('boards');
				return;
			}
		}

		this._repositionRequestsActive++;
		this.requestService
			.api('POST', '/blocks/reposition', data)
			.finally(() => {
				if (isAlive(this)) {
					this._repositionRequestsActive--;
				}
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));

		// reset to empty
		this.positionsToUpdate = [];
	}

	updateAndSavePosition(block: Block, position: GridPosition): void {
		if (!block) {
			return;
		}

		// avoiding double-render warning
		next(() => {
			// set directly on the model
			block.directPropUpdate({
				posX: position.x,
				posY: position.y,
				width: position.w,
				height: position.h,
				posMigrated: true,
			});

			this.updateBlockPositionOnGrid(block);
		});

		// "migration"
		const savedPosition: RecordData.Block.Position = Object.assign(position, { m: true });

		const blockId = block.numericId;
		if (blockId) {
			const data = { blockId, position: savedPosition };
			this.positionsToUpdate = this.positionsToUpdate.filter(
				(p) => p.blockId !== data.blockId,
			);
			this.positionsToUpdate.push(data);
			once(this, this.updatePositions);
		}
	}

	@action
	setBlockPositionForGrid(block: Block, node: HTMLElement): void {
		const el = $(node);

		// we set these manually so block positions are not auto-saved
		const attrs = block.getPositioningProperties(this.args.isPortraitView);
		el.attr({
			'data-gb-x': attrs.x,
			'data-gb-y': attrs.y,
			'data-gb-width': attrs.w,
			'data-gb-height': attrs.h,
		});

		// newly added blocks get added to the grid system here (avoid whole grid reload)
		// the blocks that are rendered on load are not added here bcos activeDatawall is not initialized yet
		if (this.activeDatawall) {
			this.activeDatawall.addElement(el);
			next(this, this.fixInvalidBlockPositions);
		}
	}

	@action
	updateBlockPositionOnGrid(block: Block): void {
		// makes sure block position on grid is synced with the one on model
		// important on outer update (e.g. over pusher when other user changes position)
		// otherwise gridboard will trigger onChange with the old values and overwrite the external changes
		if (this.activeDatawall?.grid) {
			const attrs = block.getPositioningProperties(this.args.isPortraitView);
			this.activeDatawall.updateBlockPosition(String(block.localId), attrs);
			next(this, this.fixInvalidBlockPositions);
		}
	}

	// ------ Position fixing ------

	fixInvalidBlockPositions(): void {
		if (!isAlive(this)) {
			return;
		}

		// fix the ones without positions set first
		// happens when created whole board from metrics, or added a block mobile view or mobile app
		this.fixBlocksWithoutPositions();

		// fix the overlaying ones
		this.fixOverlappingBlocks();
	}

	fixBlocksWithoutPositions(): void {
		let blocksWoPos: Block[] = [];
		const blocksWPos: Block[] = [];

		this.blocks.forEach((block: Block) => {
			if (block.hasPositionOnWall) {
				blocksWPos.push(block);
			} else {
				blocksWoPos.push(block);
			}
		});

		if (!blocksWoPos.length) {
			return;
		}

		const datawall = new DatawallGrid();
		datawall.initGridWithoutDOM(blocksWPos);

		const updatePositionFn = (block: Block, position: GridPosition, isUpdate = false) => {
			if (isUpdate) {
				datawall.updateBlockPosition(String(block.localId), position);
			} else {
				datawall.addNodeWithoutDOM(String(block.localId), position);
			}

			blocksWoPos = blocksWoPos.filter((b) => b !== block);

			this.updateAndSavePosition(block, position);
		};

		const findSpaceAndUpdateFn = (block: Block) => {
			const freeSpace = datawall.findFreeSpace();
			if (freeSpace) {
				updatePositionFn(block, freeSpace);
			}
		};

		// if all are invalid, arrange in predefined spaces
		if (blocksWoPos.length === this.blocks.length) {
			const predefinedBlockSizes = DatawallGrid.getPredefinedBlockSizes(),
				layout = predefinedBlockSizes[this.blocks.length];

			let freeSpace: GridPosition | null = null;
			if (layout) {
				layout.forEach((size: GridSize, i: number) => {
					const candidateBlock = blocksWoPos[0];

					freeSpace = datawall.findFreeSpace(size.w, size.h);
					if (freeSpace) {
						if (candidateBlock) {
							updatePositionFn(candidateBlock, freeSpace);
						}
					} else {
						this.logger.logError(
							'No space on wall for layout',
							{ count: this.blocks.length, i },
							Severity.CRITICAL,
						);
					}
				});
			}
		}

		if (!blocksWoPos.length) {
			return;
		}

		// put the rest of them as 1x1 somewhere
		blocksWoPos.forEach(findSpaceAndUpdateFn);

		if (!blocksWoPos.length) {
			return;
		}

		// if no space, resize something
		do {
			const resizedNode = datawall.makeFreeSpace();
			if (resizedNode) {
				const resizedBlock = this.blocks.find((item) => item.localId === +resizedNode.id)!;
				updatePositionFn(
					resizedBlock,
					{
						x: resizedNode.x,
						y: resizedNode.y,
						w: resizedNode.width,
						h: resizedNode.height,
					},
					true,
				);
				Array.from(blocksWoPos).forEach(findSpaceAndUpdateFn);
			} else {
				break;
			}
		} while (blocksWoPos.length > 0);
	}

	fixOverlappingBlocks(): void {
		if (this.blocks.length > Board.maxBlocks) {
			return;
		}

		const datawall = new DatawallGrid();

		datawall.initGridWithoutDOM(this.blocks, (changes) => {
			changes.forEach((change) => {
				if (change.id) {
					const block = this.blocks.find((item) => item.localId === +change.id)!;
					this.updateAndSavePosition(block, {
						x: change.x,
						y: change.y,
						w: change.width,
						h: change.height,
					});
				}
			});
		});

		datawall.fixOverlappingPositions();
	}

	@action
	zoomChanged(): void {
		if (this.activeDatawall) {
			this.activeDatawall.triggerContainerResizedEvent();
		}
	}

	@action
	prepareGrid(): void {
		this.positionsToUpdate = [];
		this.fixInvalidBlockPositions();
		this.updatePositions();
	}

	@action
	deleteBlock(block: Block): void {
		this.mmBlocksService.promptDeleteFromBoard(block);

		const activeTourId = this.flowTourDesignerService.activeTour?.id as TourId | undefined;
		if (activeTourId && this.availableTourIds.includes(activeTourId)) {
			this.flowTourDesignerService.endTour(activeTourId);
		}
	}

	@action
	addBlockInCanvas(e: PointerEvent) {
		const targetNode = e.target as HTMLElement;
		const gridboardItem = targetNode.closest('.gridboard-item')!;
		const populateWithAINode = targetNode.closest('.populate-with-ai u')!;

		if (gridboardItem) {
			const dataset = $(gridboardItem).data('dataGbNode') as GridNode | undefined; // set by gridboard
			if (dataset) {
				const sizeAndPosition = {
					posX: +dataset.x,
					posY: +dataset.y,
					width: +dataset.width,
					height: +dataset.height,
				};

				if (populateWithAINode) {
					if (
						!this.genieBoardBuildService.isAvailable ||
						!this.genieBoardBuildService.checkCanBuild()
					) {
						return;
					}

					const board = this.board;
					const entryPoint = GenieBuildBoardEntryPoint.POPULATE_PLACEHOLDER;

					if (board && !board.isNew) {
						this.genieBoardBuildService.completeBoard(board, {
							emptySlots: 1,
							entryPoint,
							slot: sizeAndPosition,
						});
					} else {
						this.genieBoardBuildService.openCategoryPopup((category) => {
							// Passing color when in designer to prevent color change.
							void this.genieBoardBuildService.createBoardAndBuild({
								color: board?.color,
								category,
								entryPoint,
							});
						});
					}

					return;
				}

				void this.mmMetricsService.openMetricSelectPopup({
					onChange: (metricSetupData: DSSetup.MetricSetupData) => {
						this.addNewBlock(
							Object.assign(
								{
									fromMetricSetup: metricSetupData,
									fromMetricDateRange:
										this.args.boardState.commonMasterDateRange?.formula,
								},
								sizeAndPosition,
							),
						);
					},
				});
			}
		}
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
		this.initGrid();

		this.availableTourIds.forEach((tourId: TourId) => {
			this.flowTourDesignerService.bindTourCheck(tourId, this, () => {
				if (this.checkTourStartConditions()) {
					this.flowTourDesignerService.startTour(tourId);
				} else if (!this.args.boardState.selectedBlock) {
					this.selectFirstBlockForTour();
				}
			});
		});

		const tourId = TourId.DESIGNER__CHANGE_DATE_RANGE;
		this.flowTourDesignerService.bindTourCheck(tourId, this, () => {
			this.selectFirstBlockForTour();
		});
	}

	@action
	teardownGrid(): void {
		if (this.activeDatawall) {
			this.activeDatawall.destroyGrid();
			this.datawallGridModelChange();
		}

		this.availableTourIds.forEach((tourId: TourId) => {
			this.flowTourDesignerService.unbindTourCheck(tourId, this);
		});

		this.flowTourDesignerService.unbindTourCheck(TourId.DESIGNER__CHANGE_DATE_RANGE, this);
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{didUpdate (fn this.updateModel false) this.blocks}}
			{{didUpdate this.prepareGrid @boardState}}
			{{didUpdate this.zoomChanged @currentZoom}}
			{{willDestroy this.teardownGrid}}
		>

			{{#unless @isViewOnly}}
				<div
					class="placeholders gridboard"
					{{! dont add conditional classes !}}
					role="button"
					{{on "click" this.addBlockInCanvas}}
				></div>
			{{/unless}}

			<div class="blocks gridboard {{if @isPortraitView 'gridboard-portrait'}}">
				{{#each this.noRerenderSortable.renderedModel as |block|}}

					<div
						class="gridboard-item {{if block.disableResize 'resize-disabled'}}"
						data-blockid={{block.id}}
						data-order={{block.order}}
						data-localid={{block.localId}}
						data-transitioning="false"
						data-gb-id={{block.localId}}
						data-gb-min-width={{this.minSize}}
						data-gb-min-height={{this.minSize}}
						data-gb-max-width={{this.maxWidth}}
						data-gb-max-height={{this.maxHeight}}
						data-gb-no-move={{if block.model.isGenerating "true" "false"}}
						data-gb-no-resize={{if block.disableResize "true" "false"}}
						{{didInsert (fn this.setBlockPositionForGrid block)}}
						{{didUpdate
							(fn this.updateBlockPositionOnGrid block)
							block.posX
							block.posY
							block.width
							block.height
						}}
					>
						<div class="gridboard-item-content">

							{{#if this.activeDatawall}}
								{{! Render only after gridboard is initialized, for sizing reasons }}
								<BlockWrapperComponent
									@model={{block}}
									@boardState={{@boardState}}
									@colorConfig={{this.colorConfig}}
									@isWallView={{true}}
									@currentZoom={{@currentZoom}}
									@skipBlockFetchLoader={{@skipBlockFetchLoaders}}
									@skipActions={{@skipActions}}
									@onBlockDelete={{fn this.deleteBlock block}}
									@trackingOrigin={{@trackingOrigin}}
								/>
							{{/if}}

						</div>
					</div>

				{{/each}}
			</div>
		</div>
	</template>
}
